import React from 'react'
import { DefaultValues, FormProvider, useForm } from 'react-hook-form'
import { ChangeReasonForm } from 'src/Libs/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useChangeReasonFormInitializerZod } from 'src/Libs/form/hooks'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { TextField } from 'src/Libs/inputs/react-hook-form'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { z } from 'zod'
import { Stack } from '@mui/material'

export interface ReasonDialogProps<T extends z.Schema, U = Record<string, never>> {
  place?: number
  onClose?: () => void | Promise<void>
  title?: string
  description?: string | React.ReactNode
  additionalValidationSchema?: T
  additionalInitialValues?: U
  onSubmit: (values: ChangeReasonForm<U>) => Promise<void> | undefined
  closeOnSubmit?: boolean
  reasonLabel?: string
  submitLabel?: string
  cancelLabel?: string
  additionalText?: string
}

/**
 * Shared dialog component to ask for reason of change. It can be used in any case when we want to ask user for reason of change before performing some action.
 */
const ReasonDialog = <T extends z.Schema, U = Record<string, never>>({
  place,
  onClose,
  title,
  description,
  additionalValidationSchema,
  additionalInitialValues,
  onSubmit,
  closeOnSubmit = true,
  reasonLabel,
  submitLabel,
  cancelLabel,
  additionalText,
}: ReasonDialogProps<T, U>): React.ReactElement => {
  const { initialValues, validationSchema } = useChangeReasonFormInitializerZod()
  const { t } = useCustomTranslation()
  const [isLoading, setIsLoading] = React.useState(false)

  // Merge initial values with additional values
  const mergedInitialValues = {
    ...initialValues,
    ...additionalInitialValues,
  } as DefaultValues<ChangeReasonForm<U>>

  // Merge validation schemas
  const mergedValidationSchema = additionalValidationSchema ? z.intersection(validationSchema, additionalValidationSchema) : validationSchema

  const methods = useForm<ChangeReasonForm<U>>({
    defaultValues: mergedInitialValues,
    mode: 'onChange',
    resolver: zodResolver(mergedValidationSchema),
  })

  const onSave = (values: ChangeReasonForm<U>): void => {
    setIsLoading(true)
    onSubmit(values)
      ?.then(() => {
        setIsLoading(false)
        if (closeOnSubmit) {
          onClose?.()
        }
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  return (
    <FormProvider {...methods}>
      <Dialog
        place={place}
        onClose={onClose}
        title={title}
        maxWidth={'sm'}
        actions={{
          primary: {
            name: submitLabel ?? t('common.save'),
            onClick: methods.handleSubmit(onSave),
            disabled: !methods.formState.isValid,
            isLoading,
          },
          secondary: [
            {
              name: cancelLabel ?? t('common.cancel'),
              onClick: onClose,
            },
          ],
        }}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack gap={2} p={2}>
            {description}
            <TextField name={'reason'} label={reasonLabel ?? t('common.change-reason')} multiline fullWidth required />
            {additionalText}
          </Stack>
        </form>
      </Dialog>
    </FormProvider>
  )
}

export default ReasonDialog
